import { ParsedUrlQuery } from 'querystring'
import { Container } from '@mui/material'
import { FeatureFlagIdInventory, FeatureFlagIdInventoryAlerts } from 'c1gen/features'
import { InventoryV1InventoryAlertSearchSearchInventoryAlerts } from 'c1gen/permissions'
import { C1Breadcrumbs } from 'c1ui'
import { APIError } from 'components/core/errors/api'
import { PageHeader } from 'components/core/page-header'
import { C1FullPageLoadingSpinner } from 'components/core/page-loader/C1FullPageLoadingSpinner'
import { SmartTabsWithRouting, TabWithFeature } from 'components/core/tabs'
import { HasCapability, useFeatureChecker } from 'components/no-ui/capability'
import { crumbs } from 'components/no-ui/crumbs/crumbData'
import { AppEntitlements } from 'components/pages/app/details/tabs/app-entitlements'
import { AppSecrets } from 'components/pages/app/details/tabs/app-secrets/AppSecrets'
import { AppUserDetailPageHeader } from 'components/pages/app/users/details/AppUserDetailPageHeader'
import { AppUserPastGrants } from 'components/pages/app/users/details/AppUserPastGrants'
import { AppUserProfile } from 'components/pages/app/users/details/AppUserProfile'
import { InventoryAlerts } from 'components/pages/app/users/details/InventoryAlerts'
import { GrantFeedColumns } from 'components/pages/entitlement/grant-feed/GrantFeed'
import { GrantFeedDrawerButton } from 'components/pages/entitlement/grant-feed/GrantFeedDrawerButton'
import { AppUserValue } from 'lib/actions/apps/app-users'
import { GetAppRequest } from 'pbts/c1/api/app/v1/app'
import { AppUserRef, AppUserServiceGetRequest } from 'pbts/c1/api/app/v1/app_user'
import { useMemo } from 'react'
import { FormattedMessage, IntlShape, useIntl } from 'react-intl'
import { getAppUserApi } from 'redux_toolkit/app-users'
import { appGetApi } from 'redux_toolkit/apps'

type GetTabsProps = {
    intl: IntlShape
    appUser: AppUserValue
}

const getTabs = ({ intl, appUser }: GetTabsProps): TabWithFeature[] => {
    return [
        {
            key: 'details',
            title: intl.formatMessage({
                defaultMessage: 'Details',
                id: 'component_app_details_header_name_details',
            }),
            tab: <AppUserProfile appUser={appUser} />,
        },
        {
            key: 'secrets',
            title: intl.formatMessage({
                defaultMessage: 'Secrets',
                id: 'component_app_details_header_name_secrets',
            }),
            tab: <AppSecrets
                appId={appUser.appId}
                identityAppUserRefs={[
                    AppUserRef.create({ appId: appUser.appId, id: appUser.id }),
                ]}
                colsToHide={['app', 'identity']}
            />,
            feature: FeatureFlagIdInventory,
        },
        {
            key: 'grants',
            title: intl.formatMessage({
                defaultMessage: 'Grants',
                id: 'component_app_details_header_name_grants',
            }),
            tab: <AppEntitlements appId={appUser.appId} appUserId={appUser.id} />,
        },
        {
            key: 'past-grants',
            title: intl.formatMessage({
                defaultMessage: 'Past grants',
                id: 'component_app_details_header_name_past_grants',
            }),
            tab: <AppUserPastGrants appId={appUser.appId} appUserId={appUser.id} />,
        },
    ]
}

type AppUserDetailsProps = {
    id: string
    appId: string
}

const columns = [GrantFeedColumns.event, GrantFeedColumns.entitlement, GrantFeedColumns.date]

export const AppUserDetails = ({ id, appId }: AppUserDetailsProps): React.ReactElement => {
    const intl = useIntl()
    const hasInventoryAlertsFF = useFeatureChecker(FeatureFlagIdInventoryAlerts)

    const {
        data: appUser,
        isLoading: appUserLoading,
        isError: appUserError,
        error: appUserErrorData,
    } = getAppUserApi.useGet(AppUserServiceGetRequest.create({
        id,
        appId,
        expandMask: { paths: ['*'] },
    }))

    const {
        data: app,
        isError: appError,
        error: appErrorData,
    } = appGetApi.useGet(GetAppRequest.create({
        id: appId,
        includeDeleted: true,
    }))

    const filter = useMemo(() => ({ appUserRefs: [{ appId: app?.id || '', id }] }), [app, id])

    if (appUserLoading) {
        return <C1FullPageLoadingSpinner />
    }

    if (appUserError || appError) {
        return <APIError
            title={<FormattedMessage id="components_identity_details_error" defaultMessage="Error loading account details" />}
            err={appUserErrorData || appErrorData}
        />
    }

    if (!appUser) {
        return <APIError
            title={<FormattedMessage id="components_identity_details_not_found" defaultMessage="Account not found" />}
            err={{ message: 'Account not found' }}
        />
    }

    const tabChange = (newValue: string): string => `/admin/applications/${appId}/accounts/${id}/${newValue}`

    const tabFromRouterQuery = (query: ParsedUrlQuery): string | string[] | undefined => {
        const { app_user_tab } = query
        return app_user_tab
    }

    const tabs = getTabs({ intl, appUser })
    const defaultTabKey = 'details'

    return <Container>
        <PageHeader
            title={<AppUserDetailPageHeader appUser={appUser} />}
            actions={app?.id && <GrantFeedDrawerButton
                filter={filter}
                columns={columns}
            />}
            breadcrumbs={<C1Breadcrumbs breadcrumbs={[
                crumbs['/admin/applications'],
                crumbs['/admin/applications/[app_id]'](appId, app?.displayName || appId),
                crumbs['/admin/applications/[app_id]/accounts'](appId),
            ]}
            />}
        />
        {hasInventoryAlertsFF && <HasCapability permission={InventoryV1InventoryAlertSearchSearchInventoryAlerts}>
            {' '}
            <InventoryAlerts id={id} appId={appId} />
        </HasCapability>}
        <SmartTabsWithRouting
            tabs={tabs}
            onTabChange={tabChange}
            tabFromRouterQuery={tabFromRouterQuery}
            defaultTabKey={defaultTabKey}
            menuKey="appUser"
        />
    </Container>
}
